import React, { useEffect, useMemo } from "react";
import {
	BrowserRouter as Router,
	Route,
	Switch,
	Redirect,
} from "react-router-dom";

import { Layout } from "./components/Layout/Layout";
import { ROUTES } from "./configs/routes";
import { Error400 } from "./pages/nonauth/ErrorPages/Error400";
import { Error403 } from "./pages/nonauth/ErrorPages/Error403";
import { Error404 } from "./pages/nonauth/ErrorPages/Error404";
import { Error500 } from "./pages/nonauth/ErrorPages/Error500";
import { Error502 } from "./pages/nonauth/ErrorPages/Error502";
import { Error503 } from "./pages/nonauth/ErrorPages/Error503";
import { MyData } from "./pages/auth/MyData/MyData";
import { History } from "./pages/auth/MyData/History/History";
import { auth, nonAuth, unsupportedBrowserOnly } from "./services/auth";
import { Login } from "./pages/nonauth/Login/Login";
import PrivacyPolicyOverview from "./pages/nonauth/PrivacyPolicy/Overview";
import ADSSOLogout from "./pages/auth/ADSSOLogout/ADSSOLogout";
import ScrollToTop from "./components/__hoc/ScrollToTopHoc";
import { PrivacyPolicy } from "./pages/nonauth/PrivacyPolicy/PrivacyPolicy";
import { CookiePolicy } from "./pages/nonauth/CookiePolicy/CookiePolicy";
import { ErrorIE } from "./pages/nonauth/ErrorPages/ErrorIE";
import LandingPage from "./pages/nonauth/LandingPage/LandingPage";
import BdpConsignment from "./pages/nonauth/Consignment/BdpConsignment";
import AnalyticsConsignment from "./pages/nonauth/Consignment/AnalyticsConsignment";
import DiagmonConsignment from "./pages/nonauth/Consignment/DiagmonConsignment";
import { INVALID_PAGE } from "./constants/storage";
import DigitalLegacyAuth from "./pages/nonauth/DigitalLegacyAuth/DigitalLegacyAuth";
import { FeatureToggle } from "./utils/feature-toggle/FeatureToggle";
import DeepLinkChecker from "./components/__hoc/DeepLinkChecker";
import { EmailSupport } from "./utils/email-supported/EmailSupport";
import { GlobalRightsResponse } from "./pages/nonauth/GlobalRightsResponse/GlobalRightsResponse";
import withRenderTimeLogger from "./components/__hoc/RenderTimeLogger";
import withFontDetection from "./components/__hoc/withFontDetection";
import AnalyticsTracker from "./components/AnalyticsTracker/AnalyticsTracker";
import { IS_LOCALHOST } from "./constants/environment";
import { MaintenancePage } from "./components/MaintenancePage/MaintenancePage";
import { useMaintenance } from "./hooks/maintenance-page-hooks";

const AppRouter = () => {
	// Calculate the base path dynamically
	const basename = useMemo(() => {
		// If REACT_APP_ROUTER_BASENAME is set to a non-root path (from build time), use it
		// We exclude "/" because we want to handle root deployments at runtime
		if (
			process.env.REACT_APP_ROUTER_BASENAME &&
			process.env.REACT_APP_ROUTER_BASENAME !== "/"
		)
			return process.env.REACT_APP_ROUTER_BASENAME;

		// Otherwise, calculate it from the current URL at runtime
		const { pathname } = window.location;

		// If pathname contains 'preview', extract the preview path
		const previewMatch = pathname.match(/^\/preview\/(\d+)/);
		if (previewMatch) return `/preview/${previewMatch[1]}`;

		// Default to root
		return "/";
	}, []);

	// Get space-separated list of disabled features and convert to an array of string
	const envDisabledFeatures =
		process.env.REACT_APP_DISABLED_FEATURES?.split(" ") || [];

	const supportedCountries: string[] = IS_LOCALHOST
		? JSON.parse(
				process.env.REACT_APP_EMAIL_SUPPORTED_COUNTRIES_LOCAL as string
			)
		: JSON.parse(process.env.REACT_APP_EMAIL_SUPPORTED_COUNTRIES as string)
				.supportedCountries;

	// Fetch maintenance config at the router level
	const { maintenanceState, isLoading: isMaintenanceLoading } =
		useMaintenance();

	useEffect(() => {
		// need to add this during mount because 404 page doesn't unmount when the user try to deeplink from 404 page to other valid page
		sessionStorage.removeItem(INVALID_PAGE);
	}, []);

	// Show nothing while maintenance config is loading to prevent flash of content
	if (isMaintenanceLoading) {
		return null;
	}

	return (
		<Router basename={basename}>
			<AnalyticsTracker />
			<FeatureToggle disabledFeatures={envDisabledFeatures}>
				<EmailSupport supportedCountries={supportedCountries}>
					<DeepLinkChecker>
						<Layout>
							<ScrollToTop>
								<Switch>
									{/* Maintenance route - only accessible when maintenance is enabled */}
									{maintenanceState.enabled && (
										<Route
											exact
											path={ROUTES.ERROR_MAINTENANCE}
											component={withRenderTimeLogger(MaintenancePage)}
										/>
									)}
									{/* Route guard: redirect all routes to maintenance page when enabled */}
									{maintenanceState.enabled && (
										<Redirect to={ROUTES.ERROR_MAINTENANCE} />
									)}
									{/* When maintenance is disabled, redirect maintenance URL to home */}
									{!maintenanceState.enabled && (
										<Route
											exact
											path={ROUTES.ERROR_MAINTENANCE}
											render={() => <Redirect to={ROUTES.ROOT} />}
										/>
									)}
									<Route
										exact
										path={ROUTES.ROOT}
										component={nonAuth(withRenderTimeLogger(LandingPage))}
									/>
									<Route
										exact
										path={ROUTES.LOGIN}
										component={withRenderTimeLogger(Login)}
									/>
									<Route
										exact
										path={ROUTES.ERROR_400}
										component={withRenderTimeLogger(Error400)}
									/>
									<Route
										exact
										path={ROUTES.ERROR_403}
										component={withRenderTimeLogger(Error403)}
									/>

									<Route
										exact
										path={ROUTES.ERROR_500}
										component={withRenderTimeLogger(Error500)}
									/>
									<Route
										exact
										path={ROUTES.ERROR_502}
										component={withRenderTimeLogger(Error502)}
									/>
									<Route
										exact
										path={ROUTES.ERROR_503}
										component={withRenderTimeLogger(Error503)}
									/>
									<Route
										exact
										path={ROUTES.ERROR_IE}
										component={unsupportedBrowserOnly(
											withRenderTimeLogger(ErrorIE)
										)}
									/>
									<Route
										exact
										path={ROUTES.MYDATA_HISTORY}
										component={auth(withRenderTimeLogger(History))}
									/>
									<Route
										exact
										path={ROUTES.MYDATA}
										component={auth(withRenderTimeLogger(MyData))}
									/>
									<Route
										exact
										path={ROUTES.OLD_DOWNLOAD_HISTORY}
										component={auth(withRenderTimeLogger(MyData))}
									/>
									<Route
										exact
										path={ROUTES.OLD_DELETE_HISTORY}
										component={auth(withRenderTimeLogger(MyData))}
									/>
									<Route
										exact
										path={ROUTES.OLD_OPTOUT_HISTORY}
										component={auth(withRenderTimeLogger(MyData))}
									/>
									<Route
										exact
										path={ROUTES.OLD_DOWNLOAD_REQUEST}
										component={auth(withRenderTimeLogger(MyData))}
									/>
									<Route
										exact
										path={ROUTES.OLD_DELETE_REQUEST}
										component={auth(withRenderTimeLogger(MyData))}
									/>
									<Route
										exact
										path={ROUTES.OLD_OPTOUT_REQUEST}
										component={auth(withRenderTimeLogger(MyData))}
									/>
									<Route
										exact
										path={ROUTES.POLICY_OVERVIEW}
										component={nonAuth(PrivacyPolicyOverview)}
									/>
									<Route
										exact
										path={ROUTES.SUMMARY_REQUEST_DETAILS}
										component={auth(withRenderTimeLogger(MyData))}
									/>
									<Route
										exact
										path={ROUTES.MYDATA_HISTORY_REQUEST_DETAILS}
										component={auth(withRenderTimeLogger(History))}
									/>
									<Route
										exact
										path={ROUTES.OLD_REQUEST_DETAILS}
										component={auth(withRenderTimeLogger(History))}
									/>
									<Route
										exact
										path={ROUTES.PRIVACY_POLICY}
										component={nonAuth(withRenderTimeLogger(PrivacyPolicy))}
									/>

									<Route
										exact
										path={ROUTES.COOKIE_POLICY}
										component={nonAuth(withRenderTimeLogger(CookiePolicy))}
									/>

									<Route
										exact
										path={ROUTES.ABOUT_GRPS}
										component={nonAuth(
											withRenderTimeLogger(GlobalRightsResponse)
										)}
									/>
									<Route
										exact
										path={[
											ROUTES.OLD_CONSIGNMENT_WITH_KR,
											ROUTES.OLD_CONSIGNMENT,
											ROUTES.BDP_CONSIGNMENT,
										]}
										component={nonAuth(withRenderTimeLogger(BdpConsignment))}
									/>
									<Route
										exact
										path={ROUTES.ANALYTICS_CONSIGNMENT}
										component={nonAuth(
											withRenderTimeLogger(AnalyticsConsignment)
										)}
									/>
									<Route
										exact
										path={ROUTES.DIAGMON_CONSIGNMENT}
										component={nonAuth(
											withRenderTimeLogger(DiagmonConsignment)
										)}
									/>
									<Route
										exact
										path={ROUTES.DIGITAL_LEGACY_AUTH}
										component={nonAuth(withRenderTimeLogger(DigitalLegacyAuth))}
									/>
									<Route
										exact
										path={ROUTES.ADSSO_LOGOUT}
										component={nonAuth(withRenderTimeLogger(ADSSOLogout))}
									/>
									<Route component={withRenderTimeLogger(Error404)} />
								</Switch>
							</ScrollToTop>
						</Layout>
					</DeepLinkChecker>
				</EmailSupport>
			</FeatureToggle>
		</Router>
	);
};

export default withFontDetection(AppRouter, "AppRouter");
